import { memo, useEffect, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useTwinData } from '../../../../hooks/storeHooks/useTwin'
import { Heading } from '../Heading'

export interface Inputs {
  firstName: string
  lastName: string
  jobTitle: string
}

interface ProfileTabProps {
  userData: Inputs
  isFetchingKnowledge: boolean
  openNextTab: () => void
}

const ProfileTab: React.FC<ProfileTabProps> = memo(
  ({ userData, isFetchingKnowledge, openNextTab }) => {
    const { setUserData } = useTwinData()

    const { register, handleSubmit, setValue, watch } = useForm<Inputs>({
      mode: 'onChange',
    })

    const watchedValues = watch()

    const isFieldValid = (value: string) => value?.trim().length > 0

    const isFormValid = useMemo(() => {
      const { firstName, lastName, jobTitle } = watchedValues
      return [firstName, lastName, jobTitle].every(isFieldValid)
    }, [watchedValues])

    useEffect(() => {
      if (userData) {
        setValue('firstName', userData.firstName || '')
        setValue('lastName', userData.lastName || '')
        setValue('jobTitle', userData.jobTitle || '')
      }
    }, [userData, isFetchingKnowledge, setValue])

    const onSubmit: SubmitHandler<Inputs> = data => {
      setUserData(data)
      openNextTab()
    }

    const inputClassName =
      'px-4 tc-mv-blur h-10 text-white rounded-full w-full mb-2'
    const fieldValidation = {
      required: true,
      validate: (value: string) => isFieldValid(value),
    }

    return (
      <div className="w-full md:w-[600px] text-white flex flex-col items-center justify-center mb-auto sm:mb-0">
        <Heading
          title="Your Profile"
          subtitle="We’ll use this info to shape your digital twin."
        />
        <form
          className="flex flex-col w-full"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="">
            <label htmlFor="firstName" className="block mb-2 text-sm">
              First name*
            </label>
            <input
              id="firstName"
              type="text"
              className={inputClassName}
              placeholder="Enter your first name here…"
              {...register('firstName', fieldValidation)}
            />
            <label htmlFor="lastName" className="block mb-2 text-sm">
              Last name*
            </label>
            <input
              id="lastName"
              type="text"
              className={inputClassName}
              placeholder="Enter your last name here…"
              {...register('lastName', fieldValidation)}
            />
            <div className="relative">
              <label htmlFor="jobTitle" className="block mb-2 text-sm">
                Job title*
              </label>
              {isFetchingKnowledge && (
                <div className="absolute left-4  bottom-10 z-10">
                  <AnimatedSpinner />
                </div>
              )}
              <input
                id="jobTitle"
                type="text"
                className={`${isFetchingKnowledge ? 'px-12' : 'px-4'} tc-mv-blur h-10 text-white rounded-full w-full mb-8`}
                placeholder={
                  isFetchingKnowledge
                    ? 'Extracting job title...'
                    : 'Enter your job title here...'
                }
                disabled={isFetchingKnowledge}
                {...register('jobTitle', fieldValidation)}
              />
            </div>
          </div>
          <div className="mx-auto">
            <button
              data-testid="profile-continue-btn"
              disabled={!isFormValid}
              className="btn btn-primary"
            >
              Continue
              <i className="fa-solid fa-arrow-right ml-2" />
            </button>
          </div>
        </form>
      </div>
    )
  },
)

export { ProfileTab }
